import React, { useState, useEffect } from 'react';
import { Card, Input, Table, Tag, Button, Space } from 'antd';
import { ClientsApi } from 'api/api';
import { ClientBriefDto, ClientType } from 'api/models';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import FeatherIcon from 'feather-icons-react';
import { ClientForm } from '../../../clients/ClientForm';

const SearchInput = styled(Input)`
  width: 300px;
  margin-bottom: 24px;
`;

const TableCard = styled(Card)`
  .ant-card-body {
    padding: 0;
  }

  .ant-table-tbody > tr.ant-table-row-selected > td {
    background-color: #f0f5ff;
  }
`;

const TypeTag = styled(Tag)<{ $type: 'individual' | 'company' }>`
  text-transform: capitalize;
  background-color: ${props => (props.$type === 'individual' ? '#e6f4ff' : '#f6f0ff')};
  color: ${props => (props.$type === 'individual' ? '#0958d9' : '#531dab')};
  border-color: ${props => (props.$type === 'individual' ? '#91caff' : '#d3adf7')};
`;

interface Props {
  value?: number;
  onChange: (clientId: number) => void;
}

const clientsApi = new ClientsApi();

const ClientSelection: React.FC<Props> = ({ value, onChange }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState<ClientBriefDto[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [modalState, setModalState] = useState({
    visible: false,
    editMode: false,
    selectedClient: undefined,
  });
  const [newlyAddedClientId, setNewlyAddedClientId] = useState<number | null>(null);

  useEffect(() => {
    fetchClients();
  }, [searchTerm, newlyAddedClientId]); // Added newlyAddedClientId as a dependency

  const fetchClients = async () => {
    try {
      setLoading(true);
      const response = await clientsApi.apiClientsGet({
        pageSize: -1,
        searchTerm,
      });
      const fetchedClients = response.data.items || [];
      
      // Sort clients to prioritize newly added clientId or selected clientId
      const sortedClients = fetchedClients.sort((a, b) => {
        // Prioritize newly added clientId if it exists
        if (newlyAddedClientId) {
          if (a.id === newlyAddedClientId) return -1;
          if (b.id === newlyAddedClientId) return 1;
        }
        // Then prioritize selected clientId from props if it exists
        if (value) {
          if (a.id === value) return -1;
          if (b.id === value) return 1;
        }
        // Maintain original order for other clients
        return 0;
      });

      setClients(sortedClients);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetModalState = () => {
    setModalState({
      visible: false,
      editMode: false,
      selectedClient: undefined,
    });
  };

  const handleModalSubmit = async (clientId: number) => {
    setNewlyAddedClientId(clientId); // Set the newly added clientId
    resetModalState();
    onChange(clientId);
    await fetchClients(); // Fetch clients after state update
  };

  const columns = [
    {
      title: t('clients:name', 'Name'),
      key: 'name',
      render: (record: ClientBriefDto) => (
        <span>{record.type === ClientType.NUMBER_0 ? record.name : record.companyName}</span>
      ),
    },
    {
      title: t('clients:type', 'Type'),
      dataIndex: 'type',
      key: 'type',
      render: (type: ClientType) => (
        <Tag color={type === ClientType.NUMBER_0 ? 'blue' : 'purple'}>
          {type === ClientType.NUMBER_0 ? 'Individual' : 'Company'}
        </Tag>
      ),
    },
    {
      title: t('clients:personalId', 'Personal ID'),
      dataIndex: 'personalId',
      key: 'personalId',
      render: (personalId: string | null) => <span>{personalId || t('global:no-data', 'N/A')}</span>,
    },
    {
      title: t('clients:companyRegistrationId', 'Company Registration ID'),
      dataIndex: 'companyRegistrationId',
      key: 'companyRegistrationId',
      render: (registrationId: string | null) => <span>{registrationId || t('global:no-data', 'N/A')}</span>,
    },
    {
      title: t('clients:taxNumber', 'Tax Number'),
      dataIndex: 'taxNumber',
      key: 'taxNumber',
      render: (taxNumber: string | null) => <span>{taxNumber || t('global:no-data', 'N/A')}</span>,
    },
    {
      title: t('clients:email', 'Email'),
      dataIndex: 'email',
      key: 'email',
      render: (email: string | null) => <span>{email || t('global:no-data', 'N/A')}</span>,
    },
    {
      title: t('clients:phone', 'Phone'),
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
      render: (phoneNumber: string | null) => <span>{phoneNumber || t('global:no-data', 'N/A')}</span>,
    },
    {
      title: t('clients:address', 'Address'),
      dataIndex: 'address',
      key: 'address',
      render: (address: string | null) => <span>{address || t('global:no-data', 'N/A')}</span>,
    },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 24, width: '100%', justifyContent: 'space-between' }}>
        <SearchInput
          prefix={<FeatherIcon icon="search" size={16} />}
          placeholder={t('clients:search', 'Search clients...')}
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
        />
        <Button type="primary" onClick={() => setModalState({ ...modalState, visible: true })}>
          {t('clients:add-new', 'Add New Client')}
        </Button>
      </Space>

      <Table
        loading={loading}
        dataSource={clients}
        columns={columns}
        rowKey="id"
        rowSelection={{
          type: 'radio',
          selectedRowKeys: value ? [value] : [],
          onChange: selectedRowKeys => onChange(selectedRowKeys[0] as number),
        }}
        onRow={record => ({
          onClick: () => onChange(record.id!),
          style: { cursor: 'pointer' },
        })}
      />

      <ClientForm
        visible={modalState.visible}
        editMode={modalState.editMode}
        client={modalState.selectedClient}
        onCancel={resetModalState}
        onSuccess={handleModalSubmit}
      />
    </div>
  );
};

export default ClientSelection;